import { cn } from '@/lib/utils';
import { forwardRef, type HTMLAttributes, type ReactNode, type Ref } from 'react';

export interface BoardColumnShellProps extends HTMLAttributes<HTMLDivElement> {
  /** Thin category accent bar at the top of the column. Hidden when omitted. */
  accentColor?: string;
  /** Header contents (title / count / actions). Laid out in a flex row. */
  header: ReactNode;
  /** Ref forwarded to the scrollable body (useful for DnD droppable hooks). */
  bodyRef?: Ref<HTMLDivElement>;
  /** Extra classes for the scrollable body. */
  bodyClassName?: string;
}

/**
 * Presentational board-column shell — shared by task-manager and recruitment.
 * Wraps the outer frame, accent bar, header row, and scrollable body.
 * Drop/drag hooks live in the caller and attach to bodyRef.
 */
export const BoardColumnShell = forwardRef<HTMLDivElement, BoardColumnShellProps>(
  ({ accentColor, header, bodyRef, bodyClassName, className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex h-full w-72 shrink-0 flex-col overflow-hidden rounded-xl border border-border/60 bg-muted/30',
        className,
      )}
      {...rest}
    >
      {accentColor && (
        <div className="h-1 shrink-0" style={{ backgroundColor: accentColor }} />
      )}
      <div className="flex shrink-0 items-center gap-2 border-b border-border/40 bg-muted/40 px-3 py-2.5">
        {header}
      </div>
      <div
        ref={bodyRef}
        className={cn(
          'flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto p-2',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </div>
  ),
);
BoardColumnShell.displayName = 'BoardColumnShell';
